/**
 * MAP mode: propose, validate and commit the source-bone → canonical-bone mapping of a rigged
 * import (SPEC "Existing Rigged Model Workflow"), plus the reusable mapping presets.
 *
 * The skeleton analysis is taken from the model's BIND pose (plan D14) and cached per model, so
 * posing the character in ANIMATE can never change what a later `confirmMapping` computes.
 */

import { AppError } from '../../shared';
import type { CanonicalBoneId } from '../../shared';
import { presetsRepo } from '../../persistence';
import { bindRig } from '../../runtime';
import { analyzeSkeleton } from '../../rig/sourceSkeleton';
import type { SkeletonAnalysis } from '../../rig/sourceSkeleton';
import { autoMapSkeleton, guessWorldCorrection } from '../../rig/mapping';
import type { MappingConfidence } from '../../rig/mapping';
import { buildBinding, applyPreset, presetFromMapping, validateMapping } from '../../rig/mappingValidation';
import type { ImportedModel } from '../../importExport';
import type { ControllerDeps } from './internal';
import { guard, guardSync, requireModel } from './internal';
import type { MappingController } from './types';

export function createMappingController(deps: ControllerDeps): MappingController {
  const { ctx } = deps;
  const { store, session, events } = ctx;

  /**
   * One analysis per imported model. `analyzeSkeleton` reads `skeleton.boneInverses`, i.e. the
   * bind pose, but it also falls back to live world matrices for bones no skin mentions — caching
   * on the model keeps even that fallback anchored to import time.
   */
  const analyses = new WeakMap<ImportedModel, SkeletonAnalysis>();

  function analysisOf(model: ImportedModel): SkeletonAnalysis {
    let analysis = analyses.get(model);
    if (!analysis) {
      analysis = analyzeSkeleton(model.root);
      analyses.set(model, analysis);
    }
    if (analysis.bones.length === 0) {
      throw new AppError(
        'NO_SOURCE_SKELETON',
        'This model has no skeleton to map. Use RIG mode to build one instead.',
      );
    }
    return analysis;
  }

  function allConfident(
    mapping: Partial<Record<CanonicalBoneId, string>>,
  ): Partial<Record<CanonicalBoneId, MappingConfidence>> {
    const confidence: Partial<Record<CanonicalBoneId, MappingConfidence>> = {};
    for (const bone of Object.keys(mapping) as CanonicalBoneId[]) {
      if (mapping[bone]) confidence[bone] = 'confident';
    }
    return confidence;
  }

  const controller: MappingController = {
    autoMap(): void {
      guardSync(ctx, 'Automatic bone mapping failed.', () => {
        const model = requireModel(ctx);
        const analysis = analysisOf(model);
        const auto = autoMapSkeleton(analysis.bones);

        store.batch(() => {
          store.actions.setSourceBones(analysis.bones.map((b) => b.name));
          store.actions.setMappingDraft(auto.canonicalToSourceBone, auto.confidence);
          store.actions.setWorldCorrection(
            guessWorldCorrection(analysis.bones, auto.canonicalToSourceBone),
          );
        });
      });
      controller.validate();
    },

    validate(): void {
      guardSync(ctx, 'The bone mapping could not be validated.', () => {
        const model = requireModel(ctx);
        const analysis = analysisOf(model);
        const result = validateMapping(store.getState().mapping.draft, analysis.bones);
        store.actions.setMappingValidation({
          ok: result.ok,
          errors: result.errors,
          warnings: result.warnings,
        });
      });
    },

    async confirmMapping(): Promise<void> {
      return guard(ctx, 'The rig could not be bound.', async () => {
        const model = requireModel(ctx);
        const analysis = analysisOf(model);
        const state = store.getState();

        const validation =
          state.mapping.validation ?? validateMapping(state.mapping.draft, analysis.bones);
        if (!validation.ok) {
          store.actions.setMappingValidation({
            ok: false,
            errors: validation.errors,
            warnings: validation.warnings,
          });
          throw new AppError(
            'MAPPING_INVALID',
            validation.errors.length > 0
              ? `The bone mapping is not usable yet: ${validation.errors[0].message}`
              : 'The bone mapping is not usable yet.',
            { errors: validation.errors },
          );
        }

        const binding = buildBinding(
          state.mapping.draft,
          analysis.bones,
          state.mapping.worldCorrection,
        );
        const rig = bindRig(model.root, binding);
        session.setRig(rig);
        // `rigBound` mirrors `session.rig` for the pure mode gate (EXPORT); dispatched next to the
        // session call so the two cannot drift apart.
        store.actions.setRigBound(true);
        rig.resetToRest();

        store.actions.setMappingValidation({
          ok: true,
          errors: [],
          warnings: validation.warnings,
        });
        events.emit('rig-changed');
        await deps.controllers.project.saveProjectState();

        const bound = rig.mappedBoneIds().length;
        store.actions.toast('info', `Rig bound: ${bound} bones.`);
      });
    },

    async savePreset(name: string): Promise<void> {
      return guard(ctx, 'The mapping preset could not be saved.', async () => {
        const trimmed = name.trim();
        if (!trimmed) {
          throw new AppError('INVALID_PRESET', 'A mapping preset needs a name.');
        }
        const preset = presetFromMapping(trimmed, store.getState().mapping.draft);
        await presetsRepo.put(preset);
        await controller.refreshPresets();
        store.actions.toast('info', `Mapping preset "${trimmed}" saved.`);
      });
    },

    async loadPreset(id: string): Promise<void> {
      return guard(ctx, 'The mapping preset could not be applied.', async () => {
        const preset = await presetsRepo.get(id);
        if (!preset) {
          throw new AppError('PRESET_NOT_FOUND', `No mapping preset with id "${id}".`, { id });
        }
        const model = requireModel(ctx);
        const analysis = analysisOf(model);
        const { mapping, unresolved } = applyPreset(preset, analysis.bones);

        store.actions.setMappingDraft(mapping, allConfident(mapping));
        if (unresolved.length > 0) {
          store.actions.toast(
            'info',
            `${unresolved.length} bone(s) in "${preset.name}" do not exist on this model: ` +
              `${unresolved.join(', ')}.`,
          );
        }
        controller.validate();
      });
    },

    async deletePreset(id: string): Promise<void> {
      return guard(ctx, 'The mapping preset could not be deleted.', async () => {
        await presetsRepo.remove(id);
        await controller.refreshPresets();
      });
    },

    async refreshPresets(): Promise<void> {
      return guard(ctx, 'The mapping presets could not be read.', async () => {
        store.actions.setPresets(await presetsRepo.list());
      });
    },
  };

  return controller;
}
